import { List, FolderOpen } from 'lucide-react';
import { IssueGroup } from './IssueGroup';
import type { Issue } from '@/types/reportApp';

interface FileGroupedViewProps {
  issues: Issue[];
  editingIssue: string | null;
  readOnly?: boolean;
  onEdit: (id: string) => void;
  onCancelEdit: () => void;
  onUpdate: (issueId: string, updates: Partial<Issue>) => void;
  onApprove: (issueId: string) => void;
  onMoveToPending: (issueId: string) => void;
  onDismiss: (issueId: string) => void;
}

export function FileGroupedView({
  issues,
  editingIssue,
  readOnly = false,
  onEdit,
  onCancelEdit,
  onUpdate,
  onApprove,
  onMoveToPending,
  onDismiss,
}: FileGroupedViewProps) {
  const fileGroups = new Map<string, Issue[]>();
  const crossFileIssues: Issue[] = [];
  const generalIssues: Issue[] = [];

  issues.forEach(issue => {
    if (!issue.fileNames || issue.fileNames.length === 0) {
      // General issues not associated with specific files
      generalIssues.push(issue);
    } else if (issue.fileNames.length > 1) {
      // Cross-file issues
      crossFileIssues.push(issue);
    } else {
      // Single file issues
      const fileName = issue.fileNames[0];
      if (!fileGroups.has(fileName)) {
        fileGroups.set(fileName, []);
      }
      fileGroups.get(fileName)!.push(issue);
    }
  });

  return (
    <div className="space-y-4">
      {/* General issues (not associated with specific files) */}
      {generalIssues.length > 0 && (
        <IssueGroup
          title="General Issues"
          icon={List}
          issues={generalIssues}
          editingIssue={editingIssue}
          readOnly={readOnly}
          onEdit={onEdit}
          onCancelEdit={onCancelEdit}
          onUpdate={onUpdate}
          onApprove={onApprove}
          onMoveToPending={onMoveToPending}
          onDismiss={onDismiss}
        />
      )}

      {/* Cross-file issues */}
      {crossFileIssues.length > 0 && (
        <IssueGroup
          title="Cross-File Issues"
          icon={FolderOpen}
          issues={crossFileIssues}
          editingIssue={editingIssue}
          readOnly={readOnly}
          onEdit={onEdit}
          onCancelEdit={onCancelEdit}
          onUpdate={onUpdate}
          onApprove={onApprove}
          onMoveToPending={onMoveToPending}
          onDismiss={onDismiss}
        />
      )}

      {/* Single file issues grouped by file */}
      {Array.from(fileGroups.entries()).map(([fileName, fileIssues]) => (
        <IssueGroup
          key={fileName}
          title={fileName}
          icon={FolderOpen}
          issues={fileIssues}
          editingIssue={editingIssue}
          readOnly={readOnly}
          onEdit={onEdit}
          onCancelEdit={onCancelEdit}
          onUpdate={onUpdate}
          onApprove={onApprove}
          onMoveToPending={onMoveToPending}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}
